/**
 * Custom hook for wallet system
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { walletAPI, exchangeRateAPI } from '@/lib/api';
import { useToast } from '@/hooks/useToast';
import { useUser } from '@/contexts/UserContext';

export function useWalletSystem() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useUser();

  // Get current user's wallet
  const { data: wallet, isLoading: isLoadingWallet } = useQuery({
    queryKey: ['wallet', user?.customerCode || user?.supplierCode],
    queryFn: async () => {
      const customerCode = user?.customerCode || user?.supplierCode;
      if (!customerCode) return null;
      const response = await walletAPI.getWallet(customerCode);
      return response.data;
    },
    enabled: !!(user?.customerCode || user?.supplierCode),
  });

  // Get all wallets (admin only)
  const { data: allWallets, isLoading: isLoadingAllWallets } = useQuery({
    queryKey: ['wallets', 'all'],
    queryFn: async () => {
      const response = await walletAPI.getAllWallets();
      return response.data || [];
    },
    enabled: user?.role === 'admin',
  });

  // Get wallet transactions
  const { data: transactions, isLoading: isLoadingTransactions } = useQuery({
    queryKey: ['wallet-transactions', user?.customerCode || user?.supplierCode],
    queryFn: async () => {
      const customerCode = user?.customerCode || user?.supplierCode;
      if (!customerCode) return [];
      const response = await walletAPI.getTransactions(customerCode, 50);
      return response.data || [];
    },
    enabled: !!(user?.customerCode || user?.supplierCode),
  });

  // Get exchange rates
  const { data: exchangeRates } = useQuery({
    queryKey: ['exchange-rates'],
    queryFn: async () => {
      const response = await exchangeRateAPI.getRates();
      return response.data || [];
    },
  });

  // Add credit (admin only)
  const addCreditMutation = useMutation({
    mutationFn: walletAPI.addCredit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallets'] });
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
      toast({
        title: 'Success',
        description: 'Credit added successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add credit',
        variant: 'destructive',
      });
    },
  });

  // Transfer money
  const transferMutation = useMutation({
    mutationFn: walletAPI.transfer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
      toast({
        title: 'Success',
        description: 'Transfer completed successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Transfer failed',
        variant: 'destructive',
      });
    },
  });

  // Exchange currency
  const exchangeMutation = useMutation({
    mutationFn: walletAPI.exchange,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
      toast({
        title: 'Success',
        description: 'Currency exchanged successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Exchange failed',
        variant: 'destructive',
      });
    },
  });

  // Pay invoice
  const payInvoiceMutation = useMutation({
    mutationFn: walletAPI.payInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast({
        title: 'Success',
        description: 'Invoice paid successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Payment failed',
        variant: 'destructive',
      });
    },
  });

  // Pay sourcing request
  const paySourcingRequestMutation = useMutation({
    mutationFn: walletAPI.paySourcingRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['sourcing-requests'] });
      toast({
        title: 'Success',
        description: 'Sourcing request paid successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Payment failed',
        variant: 'destructive',
      });
    },
  });

  // Update exchange rate (admin only)
  const updateExchangeRateMutation = useMutation({
    mutationFn: exchangeRateAPI.updateRate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exchange-rates'] });
      toast({
        title: 'Success',
        description: 'Exchange rate updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update exchange rate',
        variant: 'destructive',
      });
    },
  });

  // Get exchange rate for currency pair
  const getExchangeRate = (fromCurrency: string, toCurrency: string): number => {
    if (!exchangeRates) return 1;
    const rate = exchangeRates.find(
      (r: any) => r.from_currency === fromCurrency && r.to_currency === toCurrency
    );
    return rate?.rate || 1;
  };

  return {
    // Current user wallet
    wallet,
    isLoadingWallet,
    
    // Admin - all wallets
    allWallets,
    isLoadingAllWallets,
    
    // Transactions
    transactions,
    isLoadingTransactions,
    
    // Exchange rates
    exchangeRates,
    getExchangeRate,
    
    // Mutations
    addCredit: addCreditMutation.mutate,
    transfer: transferMutation.mutate,
    exchange: exchangeMutation.mutate,
    payInvoice: payInvoiceMutation.mutate,
    paySourcingRequest: paySourcingRequestMutation.mutate,
    updateExchangeRate: updateExchangeRateMutation.mutate,
    
    // Loading states
    isAddingCredit: addCreditMutation.isPending,
    isTransferring: transferMutation.isPending,
    isExchanging: exchangeMutation.isPending,
    isPayingInvoice: payInvoiceMutation.isPending,
    isPayingSourcingRequest: paySourcingRequestMutation.isPending,
    isUpdatingExchangeRate: updateExchangeRateMutation.isPending,
  };
}
